import React, { useEffect, useRef, useState } from 'react';
import { Network } from 'vis-network';
import { DataSet } from 'vis-data';

const Mindmap = () => {
  const networkRef = useRef(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [showSidebar, setShowSidebar] = useState(false);

  // 객관적 NCS 데이터 (산업현장 조사 + 훈련시간 기반)
  const objectiveNCSData = {
    'LM2001020201': { importance: 55.0, usage: 39.0, trainingHours: 40, level: 5, difficulty: 26.0 },
    'LM2001020205': { importance: 40.0, usage: 16.0, trainingHours: 48, level: 5, difficulty: 30.0 },
    'LM2001020211': { importance: 40.0, usage: 32.0, trainingHours: 60, level: 5, difficulty: 18.0 },
    'LM2001020212': { importance: 21.0, usage: 7.0, trainingHours: 40, level: 5, difficulty: 10.0 },
    'LM2001020214': { importance: 36.0, usage: 40.0, trainingHours: 30, level: 3, difficulty: 16.0 },
    'LM2001020224': { importance: 43.0, usage: 66.0, trainingHours: 48, level: 5, difficulty: 28.0 },
    'LM2001020225': { importance: 35.0, usage: 41.0, trainingHours: 48, level: 3, difficulty: 7.0 },
    'LM2001020227': { importance: 58.0, usage: 81.0, trainingHours: 40, level: 3, difficulty: 43.0 },
    'LM2001020230': { importance: 31.0, usage: 26.0, trainingHours: 50, level: 3, difficulty: 13.0 },
    'LM2001020231': { importance: 34.0, usage: 50.0, trainingHours: 50, level: 2, difficulty: 9.0 },
  };

  // 객관적 데이터 기반 강도 계산
  const calculateDataDrivenStrength = (ncsId, techId) => {
    const ncs = objectiveNCSData[ncsId];
    if (!ncs) return 0.5;
    
    // 활용빈도(60%) + 중요도(40%) 가중평균으로 NCS 가중치 계산
    const ncsWeight = (ncs.usage * 0.6 + ncs.importance * 0.4) / 100;
    
    // 훈련시간 기반 가중치 (60시간 기준으로 정규화)
    const trainingWeight = Math.min(ncs.trainingHours / 60, 1.0);
    
    // 기술별 기본 관련도 (추후 더 세밀하게 조정 가능)
    const techRelevanceMatrix = {
      'LM2001020224': { // 화면설계 - 활용도 66%로 매우 높음
        'html': 1.0, 'css': 1.0, 'javascript': 0.9, 'jquery': 0.7, 'sass': 0.6
      },
      'LM2001020225': { // 화면구현 - 활용도 41%
        'react': 0.9, 'vue': 0.8, 'angular': 0.7, 'javascript': 0.9, 'typescript': 0.8,
        'html': 0.8, 'css': 0.8, 'webpack': 0.6, 'npm': 0.7
      },
      'LM2001020227': { // 테스트수행 - 활용도 81%로 최고
        'junit': 0.9, 'jest': 0.8, 'selenium': 0.8
      },
      'LM2001020211': { // 서버프로그램구현 - 훈련시간 60시간으로 최고
        'spring-boot': 0.9, 'spring': 0.9, 'java': 0.9, 'nodejs': 0.8,
        'express': 0.7, 'python': 0.6, 'django': 0.6, 'maven': 0.8, 'gradle': 0.7, 'tomcat': 0.8
      },
      'LM2001020230': { // 프로그래밍언어응용 - 기초 레벨
        'java': 0.9, 'javascript': 0.8, 'python': 0.7, 'csharp': 0.6
      },
      'LM2001020231': { // 프로그래밍언어활용 - 고급 기술
        'spring': 0.9, 'spring-boot': 0.8, 'react': 0.8, 'nodejs': 0.7, 'dotnet': 0.6, 'django': 0.5
      },
      'LM2001020201': { // 요구사항확인
        'oracle': 0.8, 'mysql': 0.7, 'apache': 0.6, 'tomcat': 0.7, 'xml': 0.6
      },
      'LM2001020205': { // 데이터입출력구현 - 활용도 낮음(16%)
        'mysql': 0.9, 'oracle': 0.9, 'postgresql': 0.8, 'mariadb': 0.7, 'mongodb': 0.6, 'redis': 0.6
      },
      'LM2001020212': { // 인터페이스구현 - 활용도 매우 낮음(7%)
        'rest': 0.9, 'soap': 0.8, 'json': 0.9, 'xml': 0.8, 'spring': 0.8, 'nodejs': 0.7, 'express': 0.6, 'graphql': 0.5
      },
      'LM2001020214': { // 애플리케이션배포 - 활용도 40%
        'docker': 0.9, 'kubernetes': 0.8, 'aws': 0.8, 'azure': 0.6, 'jenkins': 0.7,
        'git': 0.8, 'apache': 0.7, 'nginx': 0.8, 'tomcat': 0.7
      }
    };
    
    const techRelevance = techRelevanceMatrix[ncsId]?.[techId] || 0.3;
    
    return Math.min(ncsWeight * trainingWeight * techRelevance * 2, 1.0);
  };

  // NCS 모듈 데이터 (객관적 데이터로 업데이트)
  const ncsModules = [
    {
      id: 'LM2001020201',
      label: '요구사항확인',
      description: '업무 분석가가 수집․분석․정의한 요구사항과 이에 따른 분석모델에 대해서 확인과 현행 시스템에 대해 분석',
      category: 'analysis',
      level: 5,
      importance: 55.0,
      usage: 39.0,
      trainingHours: 40,
      keywords: ['요구공학', '분석모델', '시스템분석', '현행시스템', 'DBMS', '미들웨어']
    },
    {
      id: 'LM2001020205',
      label: '데이터입출력구현',
      description: '논리 데이터저장소 확인, 물리 데이터저장소 설계, 데이터 조작 프로시저 작성 및 최적화',
      category: 'database',
      level: 5,
      importance: 40.0,
      usage: 16.0,
      trainingHours: 48,
      keywords: ['데이터모델링', 'SQL', '데이터베이스', '저장프로시저', '트랜잭션']
    },
    {
      id: 'LM2001020230',
      label: '프로그래밍언어응용',
      description: '프로그래밍 언어의 특성을 이해하고 언어의 문법을 적용하여 기본 응용소프트웨어를 구현',
      category: 'programming',
      level: 3,
      importance: 31.0,
      usage: 26.0,
      trainingHours: 50,
      keywords: ['객체지향', 'API', '라이브러리', '언어문법', '기본구조']
    },
    {
      id: 'LM2001020231',
      label: '프로그래밍언어활용',
      description: '프로그래밍 언어를 활용하여 기본 응용소프트웨어를 구현',
      category: 'programming',
      level: 2,
      importance: 34.0,
      usage: 50.0,
      trainingHours: 50,
      keywords: ['고급프로그래밍', '프레임워크', '패턴', '최적화', '리팩토링']
    },
    {
      id: 'LM2001020224',
      label: '화면설계',
      description: 'UI 요구사항을 확인하고 설계하여 UI 상세 설계를 작성',
      category: 'frontend',
      level: 5,
      importance: 43.0,
      usage: 66.0,
      trainingHours: 48,
      keywords: ['UI/UX', '프로토타입', '와이어프레임', '사용성', '접근성']
    },
    {
      id: 'LM2001020225',
      label: '화면구현',
      description: 'UI 설계를 바탕으로 화면을 구현하고 구현된 화면의 동작을 점검',
      category: 'frontend',
      level: 3,
      importance: 35.0,
      usage: 41.0,
      trainingHours: 48,
      keywords: ['프론트엔드', '반응형', '사용자경험', '이벤트처리', 'DOM']
    },
    {
      id: 'LM2001020211',
      label: '서버프로그램구현',
      description: '개발환경을 구축하고 공통 모듈, 업무 프로그램과 배치 프로그램을 구현',
      category: 'backend',
      level: 5,
      importance: 40.0,
      usage: 32.0,
      trainingHours: 60,
      keywords: ['서버사이드', 'API개발', '백엔드', '배치처리', '공통모듈']
    },
    {
      id: 'LM2001020212',
      label: '인터페이스구현',
      description: '연계 시스템과의 인터페이스를 구현하고 구현된 인터페이스를 점검',
      category: 'integration',
      level: 3,
      keywords: ['시스템연계', 'API', '통신', 'EAI', 'REST', 'SOAP']
    },
    {
      id: 'LM2001020214',
      label: '애플리케이션배포',
      description: '개발이 완료된 애플리케이션의 배포 환경을 구성하고 배포',
      category: 'deployment',
      level: 4,
      keywords: ['배포', '운영', '서버관리', 'CI/CD', '모니터링']
    },
    {
      id: 'LM2001020227',
      label: '애플리케이션테스트수행',
      description: '애플리케이션 테스트케이스를 작성하고 테스트를 수행',
      category: 'testing',
      level: 3,
      importance: 58.0,
      usage: 81.0,
      trainingHours: 40,
      keywords: ['테스트', '품질보증', 'QA', '단위테스트', '통합테스트']
    },
    // 추가 NCS 모듈들
    {
      id: 'LM2001020215',
      label: '정보시스템이행',
      description: '구 시스템에서 신 시스템으로의 이행 작업',
      category: 'migration',
      level: 4,
      keywords: ['시스템이행', '데이터마이그레이션', '이행전략']
    }
  ];

  // 실무 기술 스택 (NCS 데이터 기반으로 확장)
  const techStack = [
    // 프로그래밍 언어
    { id: 'java', label: 'Java', category: 'language', popularity: 95, description: '객체지향 프로그래밍 언어, 엔터프라이즈 개발에 필수' },
    { id: 'javascript', label: 'JavaScript', category: 'language', popularity: 98, description: '웹 개발의 핵심 언어, 프론트엔드/백엔드 모두 사용' },
    { id: 'python', label: 'Python', category: 'language', popularity: 90, description: '데이터 분석과 AI에 강한 언어, 백엔드 개발에도 활용' },
    { id: 'typescript', label: 'TypeScript', category: 'language', popularity: 85, description: '타입 안전성을 제공하는 JavaScript, 대규모 프로젝트에 적합' },
    { id: 'csharp', label: 'C#', category: 'language', popularity: 82, description: 'Microsoft .NET 플랫폼 기반 언어' },
    
    // 프론트엔드 기술
    { id: 'react', label: 'React', category: 'frontend-framework', popularity: 92, description: 'Facebook에서 개발한 UI 라이브러리, 컴포넌트 기반' },
    { id: 'vue', label: 'Vue.js', category: 'frontend-framework', popularity: 80, description: '학습하기 쉬운 프론트엔드 프레임워크, 점진적 적용 가능' },
    { id: 'angular', label: 'Angular', category: 'frontend-framework', popularity: 75, description: 'Google에서 개발한 풀 프레임워크, TypeScript 기반' },
    { id: 'html', label: 'HTML5', category: 'frontend-tech', popularity: 100, description: '웹의 기본 마크업 언어, 시맨틱 요소 지원' },
    { id: 'css', label: 'CSS3', category: 'frontend-tech', popularity: 100, description: '웹 스타일링 언어, 애니메이션 및 그리드 레이아웃 지원' },
    { id: 'sass', label: 'Sass/SCSS', category: 'frontend-tech', popularity: 70, description: 'CSS 전처리기, 변수와 함수 사용 가능' },
    { id: 'jquery', label: 'jQuery', category: 'frontend-tech', popularity: 65, description: 'JavaScript 라이브러리, DOM 조작 간소화' },
    
    // 백엔드 프레임워크
    { id: 'spring', label: 'Spring Framework', category: 'backend-framework', popularity: 88, description: 'Java 기반 엔터프라이즈 프레임워크, DI 및 AOP 지원' },
    { id: 'spring-boot', label: 'Spring Boot', category: 'backend-framework', popularity: 90, description: 'Spring 기반 마이크로서비스 프레임워크, 자동 구성' },
    { id: 'nodejs', label: 'Node.js', category: 'backend-framework', popularity: 85, description: '서버사이드 JavaScript 런타임, 비동기 I/O' },
    { id: 'express', label: 'Express.js', category: 'backend-framework', popularity: 82, description: 'Node.js 웹 프레임워크, 미니멀하고 유연함' },
    { id: 'django', label: 'Django', category: 'backend-framework', popularity: 75, description: 'Python 웹 프레임워크, 빠른 개발과 깔끔한 설계' },
    { id: 'dotnet', label: '.NET', category: 'backend-framework', popularity: 80, description: 'Microsoft 플랫폼, C# 및 다양한 언어 지원' },
    
    // 데이터베이스
    { id: 'mysql', label: 'MySQL', category: 'database', popularity: 85, description: '가장 인기있는 관계형 데이터베이스, 웹 애플리케이션에 최적화' },
    { id: 'postgresql', label: 'PostgreSQL', category: 'database', popularity: 78, description: '고급 기능의 관계형 데이터베이스, JSON 지원' },
    { id: 'oracle', label: 'Oracle DB', category: 'database', popularity: 82, description: '엔터프라이즈급 관계형 데이터베이스, 대용량 처리' },
    { id: 'mongodb', label: 'MongoDB', category: 'database', popularity: 75, description: 'NoSQL 문서 데이터베이스, 유연한 스키마' },
    { id: 'redis', label: 'Redis', category: 'database', popularity: 70, description: '인메모리 키-값 저장소, 캐싱 및 세션 관리' },
    { id: 'mariadb', label: 'MariaDB', category: 'database', popularity: 68, description: 'MySQL 호환 오픈소스 데이터베이스' },
    
    // 웹 서버 및 미들웨어  
    { id: 'apache', label: 'Apache HTTP Server', category: 'server', popularity: 75, description: '세계에서 가장 널리 사용되는 웹 서버' },
    { id: 'nginx', label: 'Nginx', category: 'server', popularity: 80, description: '고성능 웹 서버 및 리버스 프록시' },
    { id: 'tomcat', label: 'Apache Tomcat', category: 'server', popularity: 85, description: 'Java 서블릿 컨테이너, JSP 엔진' },
    { id: 'iis', label: 'IIS', category: 'server', popularity: 65, description: 'Microsoft 웹 서버, Windows 환경' },
    
    // 개발 도구
    { id: 'git', label: 'Git', category: 'tool', popularity: 100, description: '분산 버전 관리 시스템, 협업 개발 필수' },
    { id: 'svn', label: 'SVN', category: 'tool', popularity: 60, description: '중앙 집중식 버전 관리 시스템' },
    { id: 'maven', label: 'Maven', category: 'tool', popularity: 75, description: 'Java 빌드 도구, 의존성 관리' },
    { id: 'gradle', label: 'Gradle', category: 'tool', popularity: 80, description: 'Java/Android 빌드 도구, Groovy/Kotlin DSL' },
    { id: 'npm', label: 'npm', category: 'tool', popularity: 95, description: 'Node.js 패키지 관리자' },
    { id: 'webpack', label: 'Webpack', category: 'tool', popularity: 85, description: '모던 JavaScript 번들러' },
    
    // 클라우드 및 DevOps
    { id: 'docker', label: 'Docker', category: 'devops', popularity: 80, description: '컨테이너화 플랫폼, 환경 일관성 보장' },
    { id: 'kubernetes', label: 'Kubernetes', category: 'devops', popularity: 75, description: '컨테이너 오케스트레이션, 자동 배포 및 확장' },
    { id: 'aws', label: 'AWS', category: 'cloud', popularity: 85, description: 'Amazon 웹 서비스, 클라우드 컴퓨팅 플랫폼' },
    { id: 'azure', label: 'Microsoft Azure', category: 'cloud', popularity: 75, description: 'Microsoft 클라우드 플랫폼' },
    { id: 'jenkins', label: 'Jenkins', category: 'devops', popularity: 70, description: 'CI/CD 자동화 도구' },
    
    // 테스트 도구
    { id: 'junit', label: 'JUnit', category: 'testing', popularity: 70, description: 'Java 테스트 프레임워크, 단위 테스트' },
    { id: 'jest', label: 'Jest', category: 'testing', popularity: 75, description: 'JavaScript 테스트 프레임워크, React 테스트에 최적화' },
    { id: 'selenium', label: 'Selenium', category: 'testing', popularity: 68, description: '웹 애플리케이션 자동 테스트 도구' },
    
    // API 및 통신
    { id: 'rest', label: 'REST API', category: 'api', popularity: 95, description: 'RESTful 웹 서비스 아키텍처' },
    { id: 'graphql', label: 'GraphQL', category: 'api', popularity: 70, description: 'API용 쿼리 언어, 유연한 데이터 페칭' },
    { id: 'soap', label: 'SOAP', category: 'api', popularity: 50, description: 'XML 기반 웹 서비스 프로토콜' },
    { id: 'json', label: 'JSON', category: 'data', popularity: 100, description: '데이터 교환 형식, JavaScript 객체 표기법' },
    { id: 'xml', label: 'XML', category: 'data', popularity: 75, description: '확장 가능한 마크업 언어, 데이터 구조화' }
  ];

  // 객관적 데이터 기반 연결 관계 생성
  const generateDataDrivenConnections = () => {
    const connections = [];
    
    // NCS 모듈별 기술 연결 정의
    const ncsToTechMappings = {
      'LM2001020201': ['oracle', 'mysql', 'apache', 'tomcat', 'xml'],
      'LM2001020230': ['java', 'javascript', 'python', 'csharp'],
      'LM2001020231': ['spring', 'spring-boot', 'react', 'nodejs', 'dotnet', 'django'],
      'LM2001020224': ['html', 'css', 'javascript', 'jquery', 'sass'],
      'LM2001020225': ['react', 'vue', 'angular', 'javascript', 'typescript', 'html', 'css', 'webpack', 'npm'],
      'LM2001020211': ['spring-boot', 'spring', 'java', 'nodejs', 'express', 'python', 'django', 'maven', 'gradle', 'tomcat'],
      'LM2001020205': ['mysql', 'oracle', 'postgresql', 'mariadb', 'mongodb', 'redis'],
      'LM2001020212': ['rest', 'soap', 'json', 'xml', 'spring', 'nodejs', 'express', 'graphql'],
      'LM2001020214': ['docker', 'kubernetes', 'aws', 'azure', 'jenkins', 'git', 'apache', 'nginx', 'tomcat'],
      'LM2001020227': ['junit', 'jest', 'selenium']
    };
    
    // 각 연결에 대해 객관적 데이터 기반으로 strength 계산
    Object.entries(ncsToTechMappings).forEach(([ncsId, techIds]) => {
      techIds.forEach(techId => {
        const strength = calculateDataDrivenStrength(ncsId, techId);
        if (strength > 0.1) { // 의미있는 연결만 포함
          connections.push({ from: ncsId, to: techId, strength });
        }
      });
    });
    
    // NCS 모듈 간 연결 (화면설계 <-> 화면구현 등)
    connections.push({ from: 'LM2001020224', to: 'LM2001020225', strength: 0.9 }); // 화면설계 -> 화면구현
    
    // 기술 간 연결도 추가 (실무에서 함께 사용되는 조합)
    const techConnections = [
      { from: 'java', to: 'spring', strength: 0.9 },
      { from: 'spring', to: 'spring-boot', strength: 0.9 },
      { from: 'java', to: 'maven', strength: 0.8 },
      { from: 'java', to: 'junit', strength: 0.8 },
      { from: 'javascript', to: 'react', strength: 0.8 },
      { from: 'javascript', to: 'nodejs', strength: 0.8 },
      { from: 'javascript', to: 'npm', strength: 0.9 },
      { from: 'nodejs', to: 'express', strength: 0.9 },
      { from: 'html', to: 'css', strength: 0.9 },
      { from: 'docker', to: 'kubernetes', strength: 0.8 },
      { from: 'rest', to: 'json', strength: 0.9 }
    ];
    
    return [...connections, ...techConnections];
  };
  
  const connections = generateDataDrivenConnections();

  useEffect(() => {
    // 노드 데이터 준비
    const nodes = new DataSet([
      // NCS 모듈 노드 (큰 노드)
      ...ncsModules.map(ncs => ({
        id: ncs.id,
        label: ncs.label,
        title: ncs.description,
        group: 'ncs',
        level: ncs.level,
        category: ncs.category,
        size: 20 + (ncs.usage || 0) / 2 + (ncs.trainingHours || 0) / 5, // 활용도와 훈련시간 기반 크기
        font: { 
          size: 14, 
          face: 'Arial', 
          color: '#FFFFFF',
          strokeWidth: 2,
          strokeColor: '#000000',
          bold: true
        },
        color: {
          background: getCategoryColor(ncs.category),
          border: '#FFFFFF',
          highlight: { background: getCategoryColor(ncs.category), border: '#FFFF00' }
        }
      })),
      
      // 기술 스택 노드 (작은 노드)
      ...techStack.map(tech => ({
        id: tech.id,
        label: tech.label,
        title: tech.description,
        group: 'tech',
        category: tech.category,
        popularity: tech.popularity,
        size: 15 + (tech.popularity / 10),
        font: { 
          size: 11, 
          face: 'Arial', 
          color: '#FFFFFF',
          strokeWidth: 2,
          strokeColor: '#000000',
          bold: true
        },
        color: {
          background: getTechColor(tech.category),
          border: '#CCCCCC',
          highlight: { background: getTechColor(tech.category), border: '#FFFF00' }
        }
      }))
    ]);

    // 엣지 데이터 준비
    const edges = new DataSet(
      connections.map(conn => ({
        from: conn.from,
        to: conn.to,
        width: conn.strength * 3,
        color: { 
          color: `rgba(100, 100, 100, ${conn.strength * 0.8})`,
          highlight: '#2B5BB4'
        },
        smooth: { type: 'continuous' }
      }))
    );

    // 네트워크 옵션 설정 (옵시디언 스타일)
    const options = {
      nodes: {
        shape: 'dot',
        font: {
          color: '#FFFFFF',
          size: 12,
          face: 'Arial',
          strokeWidth: 3,
          strokeColor: '#000000',
          bold: true
        },
        borderWidth: 2,
        shadow: {
          enabled: true,
          color: 'rgba(0,0,0,0.5)',
          size: 10,
          x: 2,
          y: 2
        }
      },
      edges: {
        smooth: {
          type: 'continuous',
          roundness: 0.2
        }
      },
      physics: {
        enabled: true,
        forceAtlas2Based: {
          gravitationalConstant: -50,
          centralGravity: 0.01,
          springConstant: 0.08,
          springLength: 100,
          damping: 0.4
        },
        maxVelocity: 50,
        solver: 'forceAtlas2Based',
        timestep: 0.35,
        stabilization: { iterations: 150 }
      },
      interaction: {
        hover: true,
        hoverConnectedEdges: true,
        selectConnectedEdges: false,
        tooltipDelay: 200
      },
      layout: {
        improvedLayout: true
      }
    };

    // 네트워크 생성
    const network = new Network(networkRef.current, { nodes, edges }, options);

    // 노드 클릭 이벤트
    network.on('click', (params) => {
      if (params.nodes.length > 0) {
        const nodeId = params.nodes[0];
        const nodeData = [...ncsModules, ...techStack].find(item => item.id === nodeId);
        setSelectedNode(nodeData);
        setShowSidebar(true);
      } else {
        setShowSidebar(false);
      }
    });

    return () => {
      network.destroy();
    };
  }, []);

  // 카테고리별 색상 함수
  const getCategoryColor = (category) => {
    const colors = {
      'analysis': '#8B5CF6',      // 보라 - 분석
      'programming': '#3B82F6',   // 파랑 - 프로그래밍  
      'frontend': '#10B981',      // 초록 - 프론트엔드
      'backend': '#F59E0B',       // 주황 - 백엔드
      'database': '#EF4444',      // 빨강 - 데이터베이스
      'integration': '#06B6D4',   // 청록 - 통합
      'deployment': '#84CC16',    // 라임 - 배포
      'testing': '#F97316',       // 주황 - 테스트
      'design': '#EC4899',        // 핑크 - 설계
      'migration': '#6366F1'      // 인디고 - 이행
    };
    return colors[category] || '#6B7280';
  };

  const getTechColor = (category) => {
    const colors = {
      'language': '#8B5CF6',           // 보라 - 프로그래밍 언어
      'frontend-framework': '#10B981', // 녹색 - 프론트엔드 프레임워크
      'frontend-tech': '#34D399',      // 연녹색 - 프론트엔드 기술
      'backend-framework': '#F59E0B',  // 주황 - 백엔드 프레임워크
      'database': '#EF4444',           // 빨강 - 데이터베이스
      'server': '#FB7185',             // 분홍 - 웹서버/미들웨어
      'tool': '#6B7280',               // 회색 - 개발도구
      'devops': '#84CC16',             // 라임 - DevOps
      'cloud': '#06B6D4',              // 청록 - 클라우드
      'testing': '#F97316',            // 주황 - 테스트
      'api': '#8B5CF6',                // 보라 - API
      'data': '#10B981'                // 녹색 - 데이터 형식
    };
    return colors[category] || '#9CA3AF';
  };

  return (
    <div className="fixed inset-0 bg-gray-900 overflow-hidden">
      {/* 헤더 */}
      <div className="absolute top-0 left-0 right-0 z-10 bg-black bg-opacity-70 backdrop-blur-sm">
        <div className="p-4">
          <h1 className="text-2xl font-bold text-white mb-2">
            🧠 NCS-실무기술 연결 마인드맵
          </h1>
          <p className="text-gray-300 text-sm">
            노드를 클릭해서 상세 정보를 확인하세요 • 드래그로 위치 조정 • 마우스 휠로 줌
          </p>
        </div>
      </div>

      {/* 네트워크 그래프 */}
      <div 
        ref={networkRef} 
        className="w-full h-full"
        style={{ background: '#1a1a1a' }}
      />

      {/* 범례 */}
      <div className="absolute bottom-4 left-4 bg-black bg-opacity-90 backdrop-blur-sm rounded-lg p-4 text-white max-w-sm hidden md:block">
        <h3 className="font-semibold mb-3 text-yellow-400">📊 객관적 데이터 기반 마인드맵</h3>
        <div className="space-y-3 text-sm">
          <div className="bg-gray-800 bg-opacity-50 p-2 rounded">
            <div className="text-xs text-yellow-300 mb-1 font-semibold">🔥 높은 활용빈도 (실무 중심)</div>
            <div className="text-xs space-y-1">
              <div>• 테스트수행: 81% (최고)</div>
              <div>• 화면설계: 66% (매우높음)</div>
              <div>• 프로그래밍활용: 50%</div>
            </div>
          </div>
          
          <div className="bg-gray-800 bg-opacity-50 p-2 rounded">
            <div className="text-xs text-blue-300 mb-1 font-semibold">⏰ 긴 훈련시간 (깊이학습)</div>
            <div className="text-xs space-y-1">
              <div>• 서버프로그램구현: 60시간</div>
              <div>• 프로그래밍언어: 50시간</div>
              <div>• 화면설계/구현: 48시간</div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <div className="text-xs text-gray-300 mb-1">NCS 모듈</div>
              <div className="space-y-1">
                <div className="flex items-center space-x-1 text-xs">
                  <div className="w-3 h-3 rounded-full" style={{backgroundColor: getCategoryColor('frontend')}}></div>
                  <span>프론트엔드</span>
                </div>
                <div className="flex items-center space-x-1 text-xs">
                  <div className="w-3 h-3 rounded-full" style={{backgroundColor: getCategoryColor('backend')}}></div>
                  <span>백엔드</span>
                </div>
                <div className="flex items-center space-x-1 text-xs">
                  <div className="w-3 h-3 rounded-full" style={{backgroundColor: getCategoryColor('testing')}}></div>
                  <span>테스트</span>
                </div>
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-300 mb-1">실무 기술</div>
              <div className="space-y-1">
                <div className="flex items-center space-x-1 text-xs">
                  <div className="w-2 h-2 rounded-full" style={{backgroundColor: getTechColor('language')}}></div>
                  <span>언어</span>
                </div>
                <div className="flex items-center space-x-1 text-xs">
                  <div className="w-2 h-2 rounded-full" style={{backgroundColor: getTechColor('frontend-framework')}}></div>
                  <span>프레임워크</span>
                </div>
                <div className="flex items-center space-x-1 text-xs">
                  <div className="w-2 h-2 rounded-full" style={{backgroundColor: getTechColor('testing')}}></div>
                  <span>테스트도구</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="text-xs text-gray-400 border-t border-gray-600 pt-2">
            <div>• 노드 크기 = 활용빈도 + 훈련시간</div>
            <div>• 연결선 두께 = 실무 관련도</div>
            <div>• 데이터 출처: NCS 산업현장 조사</div>
          </div>
        </div>
      </div>

      {/* 사이드바 */}
      {showSidebar && selectedNode && (
        <div className="absolute top-0 right-0 w-80 h-full bg-white shadow-2xl z-20 overflow-y-auto">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-800">{selectedNode.label}</h2>
              <button 
                onClick={() => setShowSidebar(false)}
                className="text-gray-500 hover:text-gray-700 text-xl"
              >
                ✕
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-gray-700 mb-2">설명</h3>
                <p className="text-gray-600 text-sm">{selectedNode.description}</p>
              </div>

              {selectedNode.category && (
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">카테고리</h3>
                  <span 
                    className="px-3 py-1 rounded-full text-xs text-white"
                    style={{ backgroundColor: selectedNode.group === 'ncs' ? getCategoryColor(selectedNode.category) : getTechColor(selectedNode.category) }}
                  >
                    {selectedNode.category}
                  </span>
                </div>
              )}

              {selectedNode.keywords && (
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">주요 키워드</h3>
                  <div className="flex flex-wrap gap-2">
                    {selectedNode.keywords.map((keyword, index) => (
                      <span key={index} className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                        {keyword}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {selectedNode.popularity && (
                <div>
                  <h3 className="font-semibold text-gray-700 mb-2">인기도</h3>
                  <div className="flex items-center space-x-2">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full"
                        style={{ width: `${selectedNode.popularity}%` }}
                      ></div>
                    </div>
                    <span className="text-sm text-gray-600">{selectedNode.popularity}%</span>
                  </div>
                </div>
              )}

              {selectedNode.group === 'ncs' && (
                <div className="bg-blue-50 p-3 rounded-lg">
                  <h3 className="font-semibold text-blue-800 mb-2">학습 자료</h3>
                  <div className="space-y-2">
                    <button className="w-full text-left bg-blue-600 text-white px-3 py-2 rounded text-sm hover:bg-blue-700">
                      📚 AI 요약 보기
                    </button>
                    <button className="w-full text-left bg-green-600 text-white px-3 py-2 rounded text-sm hover:bg-green-700">
                      💬 면접 질문 보기
                    </button>
                    <button className="w-full text-left bg-purple-600 text-white px-3 py-2 rounded text-sm hover:bg-purple-700">
                      🎵 팟캐스트 듣기
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Mindmap;